/* ══════════════════════════════════════════════════════════════════════
   KO_VOLUME + CAMPA_VOLUME parsing

   Two very different shapes:
     KO_VOLUME     long/transactional — one row per entity×brand×pack×month,
                   with Affordable and 0-20 Category flags and a volume column
     CAMPA_VOLUME  wide/pivoted — one row per BU, one column per month,
                   values formatted "418 K" / "9 M"

   Metrics (per the deck):
     affordable mix      = affordable KO volume / total KO volume
     competitive response = KO affordable volume / Campa volume
     growth              = current month vs prior month
   ══════════════════════════════════════════════════════════════════════ */

export const KO_SHEET    = /ko[_\s-]*volume/i;
export const CAMPA_SHEET = /campa[_\s-]*volume/i;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_IDX = Object.fromEntries(MONTHS.map((m, i) => [m.toLowerCase(), i]));

/* ── Market normalisation ───────────────────────────────────────────────
   BOTH sheets are keyed on the bottler-entity BU:
     KO_VOLUME     -> "BU Ship From"
     CAMPA_VOLUME  -> "BU Revised"   e.g. "KGBPL-Gujarat", "BBPL-Dankuni"
   These are DISTINCT markets, not states — "KGBPL-Gujarat" and a plain
   "Gujarat" row are different entities, so the full name is the key. The
   bottler prefix is stripped only as a SECOND-CHANCE match when the full
   names don't line up across the two sheets.                            */
const BOTTLER_PREFIX = /^(kgbpl|lbpl|bbpl|dbpl|sssl|slmg|kgb|moon|hccb|swa|fbo|superior)[-_\s/]*/i;

/* ── KO plant → Campa BU mapping ────────────────────────────────────────
   KO_VOLUME's "BU Ship From" is PLANT level (48 values); CAMPA_VOLUME's
   "BU Revised" is BU level (26). No string heuristic bridges these — the
   franchise structure has to be stated explicitly. Keys are normalised
   (lowercase, alphanumerics only), so spacing, case, en-dashes and
   "Pvt Ltd" punctuation are all irrelevant.

   Add a line here to fix any future plant. Anything absent shows up in the
   dashboard's join-diagnostics strip rather than failing silently.        */
export const KO_PLANT_TO_BU = {
  // ── Andhra Pradesh ──
  "chittoor":                                   "Andhra Pradesh",
  "chitoor":                                    "Andhra Pradesh",
  "vijayawada":                                 "Andhra Pradesh",
  "vijaywada":                                  "Andhra Pradesh",
  "vizag":                                      "Andhra Pradesh",
  "visakhapatnam":                              "Andhra Pradesh",

  // ── Telangana ──
  "hyderabad":                                  "Telangana",

  // ── Bengal / East franchisees ──
  "bengalbeveragesdankuni":                     "BBPL-Dankuni",
  "diamondbeveragestaratala":                   "DBPL-Taratala",

  // ── Kandhari group ──
  "kandharibeverageschandigarh":                "KGBPL-Chandigarh",
  "kandharibeveragespvtltdjammu":               "KGBPL-Jammu",
  "jammu":                                      "KGBPL-Jammu",
  "kandhariglobalbeveragespvtltdgujrat":        "KGBPL-Gujarat",
  "kandhariglobalbeveragespvtltdgujarat":       "KGBPL-Gujarat",
  "kandhariglobalbeveragespvtltdrajasthan":     "KGBPL-Rajasthan",
  "kandhariglobalbeveragespvtltdgurgaon":       "KGBPL-Enrich",
  "enrichagrofoodsgurgaon":                     "KGBPL-Enrich",
  "enrichdelhi":                                "KGBPL-Enrich",
  "delhi":                                "KGBPL-Enrich",
  "wavesbeveragesamritsar":                     "KGBPL-Wave",
  "wavebeveragesamritsar":                      "KGBPL-Wave",

  // ── Ludhiana ──
  "ludhianabeveragesludhiana":                  "LBPL-Ludhiana",

  // ── Moon group (NCR / West UP / Jharkhand / North East) ──
  "moonbeveragesghaziabad":                     "Moon-Ghaziabad",
  "moonbeveragesdelhi":                         "Moon-Ghaziabad",
  "moonbeverageswestup":                        "Moon-Ghaziabad",
  "westup":                                     "Moon-Ghaziabad",
  "moonbeveragesjharkhand":                     "Moon-Jharkhand",
  "moonbeveragesnortheast":                     "Moon-NER",
  "moonbeveragessiliguri":                      "Moon-NER",

  // ── SLMG (Bihar separate; the rest roll into UP/UK) ──
  "slmgbihar":                                  "SLMG - Bihar",
  "slmgdehradun":                               "SLMG-UP/UK",
  "slmggorakhpur":                              "SLMG-UP/UK",
  "slmgkanpur":                                 "SLMG-UP/UK",
  "slmgvaranasi":                               "SLMG-UP/UK",
  "slmgagra":                                   "SLMG-UP/UK",
  "slmgayodhya":                                "SLMG-UP/UK",
  "slmgbareilly":                               "SLMG-UP/UK",
  "slmglucknow":                                "SLMG-UP/UK",
  "slmgprayagraj":                              "SLMG-UP/UK",

  // ── Sarvaraya ──
  "srisarvarayarajmundry":                      "SSSL-Rajmundry",
  "srisarvarayarajahmundry":                    "SSSL-Rajmundry",

  // ── Superior Drinks (Nagpur, Bilaspur, Jabalpur, Udaipur) ──
  "superiordrinksnagpur":                       "Superior Drinks",
  "narmadadrinksbilaspur":                      "Superior Drinks",
  "udaipurbeveragesltdjabalpur":                "Superior Drinks",
  "udaipurbeveragesjabalpur":                   "Superior Drinks",

  // ── HCCB company-owned territories ──
  "goa":                                        "Goa",
  "gujarat":                                    "Gujarat",
  "sanand":                                     "Gujarat",          // Sanand plant, Gujarat
  "karnataka":                                  "Karnataka",
  "kerala":                                     "Kerala",
  "madhyapradesh":                              "Madhya Pradesh",
  "maharashtra":                                "Maharashtra",
  "mumbai":                                     "Maharashtra",
  "nasik":                                      "Maharashtra",
  "nashik":                                     "Maharashtra",
  "pune":                                       "Maharashtra",
  "odisha":                                     "Orissa",
  "orissa":                                     "Orissa",
  "tamilnadu":                                  "Tamil Nadu",

  // "Delhi" on its own is ambiguous — Moon, Enrich and HCCB all ship from
  // Delhi and Campa has no standalone Delhi BU. Left unmapped deliberately;
  // it surfaces in the diagnostics strip. Point it somewhere with one line:
  //   "delhi": "Moon-Ghaziabad",
};

/* Canonical BU list from CAMPA_VOLUME, for diagnostics */
export const CAMPA_BUS = [
  "Andhra Pradesh", "BBPL-Dankuni", "DBPL-Taratala", "Goa", "Gujarat",
  "Karnataka", "Kerala", "KGBPL-Chandigarh", "KGBPL-Enrich", "KGBPL-Gujarat",
  "KGBPL-Jammu", "KGBPL-Rajasthan", "KGBPL-Wave", "LBPL-Ludhiana",
  "Madhya Pradesh", "Maharashtra", "Moon-Ghaziabad", "Moon-Jharkhand",
  "Moon-NER", "Orissa", "SLMG - Bihar", "SLMG-UP/UK", "SSSL-Rajmundry",
  "Superior Drinks", "Tamil Nadu", "Telangana", "All India",
];

const TOTAL_ALIAS = { allindia: "allindia", india: "allindia", total: "allindia",
                      grandtotal: "allindia" };

/** Normalised lookup key: lowercase, alphanumerics only. */
export function normKey(name) {
  return String(name || "")
    .replace(/[\u2010-\u2015]/g, "-")     // en/em dashes -> hyphen
    .toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Market key. An explicit plant→BU mapping wins over the raw name. */
export function marketKey(name) {
  const k = normKey(name);
  if (TOTAL_ALIAS[k]) return TOTAL_ALIAS[k];
  const bu = KO_PLANT_TO_BU[k];
  return bu ? normKey(bu) : k;
}

/** Did this name resolve through the explicit mapping table? */
export function mappedBU(name) {
  return KO_PLANT_TO_BU[normKey(name)] || null;
}

/** Second-chance key with the bottler prefix removed ("KGBPL-Gujarat" -> "gujarat"). */
export function marketAltKey(name) {
  const stripped = String(name || "").trim().replace(BOTTLER_PREFIX, "");
  return normKey(stripped);
}

export function prettyMarket(name) {
  return String(name || "").trim().replace(/\s+/g, " ");
}

/* ── Value coercion ─────────────────────────────────────────────────── */
export function parseVol(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v).trim().replace(/,/g, "");
  const m = s.match(/^(-?[\d.]+)\s*([kKmMbB])?$/);
  if (!m) { const n = Number(s); return isNaN(n) ? 0 : n; }
  const n = Number(m[1]);
  if (isNaN(n)) return 0;
  const suf = (m[2] || "").toLowerCase();
  return suf === "k" ? n * 1e3 : suf === "m" ? n * 1e6 : suf === "b" ? n * 1e9 : n;
}

const truthy = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "y" || s === "yes" || s === "true" || s === "affordable";
};

/* ── Header helpers ─────────────────────────────────────────────────── */
function findHeaderRow(raw, probe) {
  for (let i = 0; i < Math.min(15, raw.length); i++) {
    const row = (raw[i] || []).map(c => String(c ?? "").trim());
    if (row.some(c => probe.test(c))) return i;
  }
  return -1;
}
const col = (headers, re) => headers.findIndex(h => re.test(h));

/* ══════════════════════════════════════════════════════════════════════
   KO_VOLUME
   ══════════════════════════════════════════════════════════════════════ */
export function parseKoVolume(raw) {
  const headerIdx = findHeaderRow(raw, /^l0\s*-\s*(entity|brand|cluster)$|^affordable$/i);
  if (headerIdx < 0) return null;
  const headers = (raw[headerIdx] || []).map(h => String(h ?? "").trim());

  const iCluster  = col(headers, /l0\s*-\s*cluster/i);
  const iEntity   = col(headers, /l0\s*-\s*entity/i);
  const iBU       = col(headers, /bu\s*ship\s*from|bu\s*revised|^bu$/i);
  const iBrand    = col(headers, /brand\s*revised/i) >= 0
                      ? col(headers, /brand\s*revised/i) : col(headers, /l0\s*-\s*brand/i);
  const iCategory = col(headers, /l0\s*-\s*category/i);
  const iPack     = col(headers, /retail\s*container\s*category/i) >= 0
                      ? col(headers, /retail\s*container\s*category/i)
                      : col(headers, /retail\s*container\b/i);
  const iAfford   = col(headers, /^affordable$/i);
  const i020      = col(headers, /0\s*-\s*20\s*categ/i);
  const iYear     = col(headers, /^year$/i);
  const iMonth    = col(headers, /^month/i);

  // Volume column: prefer an explicit vol/UC header, else the last numeric
  // column. Word boundaries are essential — a bare /uc/ matches the "uc" in
  // "L0 - Beverage Product" and silently picks a text column.
  let iVol = headers.findIndex(h =>
    /\b(vol|volume|uc|qty|quantity|cases)\b/i.test(h) && !/categ|container|type|pack/i.test(h));
  if (iVol < 0) {
    const sample = raw.slice(headerIdx + 1, headerIdx + 40);
    for (let c = headers.length - 1; c >= 0; c--) {
      if ([iAfford, i020, iYear].includes(c)) continue;
      const vals = sample.map(r => r?.[c]).filter(v => v !== null && v !== undefined && v !== "");
      if (vals.length && vals.every(v => !isNaN(Number(String(v).replace(/,/g, ""))))) { iVol = c; break; }
    }
  }
  if (iVol < 0 || (iBU < 0 && iCluster < 0 && iEntity < 0)) return null;

  const rows = [];
  for (let i = headerIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    if (!r || !r.some(c => c !== null && c !== undefined && c !== "")) continue;

    // Market == "BU Ship From". Cluster/entity are only fallbacks for files
    // that omit it entirely.
    const marketRaw = (iBU >= 0 && r[iBU]) || (iCluster >= 0 && r[iCluster]) || (iEntity >= 0 && r[iEntity]);
    if (!marketRaw) continue;
    const vol = parseVol(r[iVol]);
    if (!vol) continue;

    const mon = String(r[iMonth] ?? "").trim().slice(0, 3);
    rows.push({
      market:     mappedBU(marketRaw) || prettyMarket(marketRaw),
      plant:      prettyMarket(marketRaw),
      mapped:     !!mappedBU(marketRaw),
      key:        marketKey(marketRaw),
      altKey:     marketAltKey(marketRaw),
      brand:      iBrand    >= 0 ? String(r[iBrand] ?? "").trim()    : "",
      category:   iCategory >= 0 ? String(r[iCategory] ?? "").trim() : "",
      pack:       iPack     >= 0 ? String(r[iPack] ?? "").trim()     : "",
      affordable: iAfford   >= 0 ? truthy(r[iAfford]) : false,
      is020:      i020      >= 0 ? truthy(r[i020])    : false,
      year:       iYear     >= 0 ? String(r[iYear] ?? "").trim()     : "",
      month:      mon ? mon[0].toUpperCase() + mon.slice(1).toLowerCase() : "",
      monthIdx:   MONTH_IDX[mon.toLowerCase()] ?? -1,
      vol,
    });
  }
  if (!rows.length) return null;
  return { rows, headers, mapping: { iCluster, iBU, iBrand, iPack, iAfford, i020, iVol, iMonth } };
}

/* ══════════════════════════════════════════════════════════════════════
   CAMPA_VOLUME  (wide: BU × month)
   ══════════════════════════════════════════════════════════════════════ */
export function parseCampaVolume(raw) {
  const headerIdx = findHeaderRow(raw, /^bu\s*revised$|^bu$|^market$/i);
  if (headerIdx < 0) return null;
  const headers = (raw[headerIdx] || []).map(h => String(h ?? "").trim());

  const iBU = headers.findIndex(h => /^bu\s*revised$|^bu$|^market$/i.test(h));
  if (iBU < 0) return null;

  const monthCols = [];
  headers.forEach((h, idx) => {
    const m = String(h).trim().slice(0, 3).toLowerCase();
    if (MONTH_IDX[m] !== undefined) monthCols.push({ idx, month: MONTHS[MONTH_IDX[m]], monthIdx: MONTH_IDX[m] });
  });
  if (!monthCols.length) return null;

  const rows = [];
  for (let i = headerIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    if (!r || !r.some(c => c !== null && c !== undefined && c !== "")) continue;
    const bu = r[iBU];
    if (typeof bu !== "string" || !bu.trim()) continue;

    monthCols.forEach(mc => {
      const vol = parseVol(r[mc.idx]);
      if (!vol) return;
      rows.push({
        market: prettyMarket(bu), key: marketKey(bu), altKey: marketAltKey(bu),
        month: mc.month, monthIdx: mc.monthIdx, vol,
      });
    });
  }
  if (!rows.length) return null;
  return { rows, months: monthCols.map(m => m.month) };
}

/* ══════════════════════════════════════════════════════════════════════
   Workbook entry point
   ══════════════════════════════════════════════════════════════════════ */
export function hasKoCampaSheets(workbook) {
  return workbook.SheetNames.some(n => KO_SHEET.test(n))
      || workbook.SheetNames.some(n => CAMPA_SHEET.test(n));
}

export function parseKoCampaWorkbook(workbook, XLSX) {
  const koName    = workbook.SheetNames.find(n => KO_SHEET.test(n));
  const campaName = workbook.SheetNames.find(n => CAMPA_SHEET.test(n));
  if (!koName && !campaName) return null;

  const read = (name) => name
    ? XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: true, defval: null })
    : null;

  const ko    = koName    ? parseKoVolume(read(koName))       : null;
  const campa = campaName ? parseCampaVolume(read(campaName)) : null;
  if (!ko && !campa) return null;

  /* ── Join the two sheets on BU ──────────────────────────────────────
     Pass 1: exact BU name  ("KGBPL-Gujarat" == "KGBPL-Gujarat")
     Pass 2: bottler-stripped, only when pass 1 found nothing for that BU
             (covers a sheet writing "Gujarat" where the other says
             "KGBPL-Gujarat"). Campa's name always wins as the canonical
             label so the scorecard reads like the deck.                */
  if (ko && campa) {
    const campaByKey = new Map();
    const campaByAlt = new Map();
    campa.rows.forEach(r => {
      if (!campaByKey.has(r.key)) campaByKey.set(r.key, r.key);
      if (!campaByAlt.has(r.altKey)) campaByAlt.set(r.altKey, r.key);
    });
    ko.rows.forEach(r => {
      if (campaByKey.has(r.key)) return;                       // exact hit
      const viaAlt = campaByAlt.get(r.key) || campaByAlt.get(r.altKey);
      if (viaAlt) { r.matchedVia = "bottler-stripped"; r.key = viaAlt; }
    });
  }

  const months = [...new Set([
    ...(ko ? ko.rows.map(r => r.monthIdx) : []),
    ...(campa ? campa.rows.map(r => r.monthIdx) : []),
  ])].filter(i => i >= 0).sort((a, b) => a - b).map(i => MONTHS[i]);

  // Campa's label wins — it carries the canonical BU spelling
  const label = {};
  (ko?.rows || []).forEach(r => { if (!label[r.key]) label[r.key] = r.market; });
  (campa?.rows || []).forEach(r => { label[r.key] = r.market; });

  const koKeys    = new Set((ko?.rows || []).map(r => r.key));
  const campaKeys = new Set((campa?.rows || []).map(r => r.key));

  return {
    koRows:    ko?.rows    || [],
    campaRows: campa?.rows || [],
    months,
    label,
    marketKeys: [...new Set([...koKeys, ...campaKeys])].filter(k => k !== "allindia"),
    matched:   [...koKeys].filter(k => campaKeys.has(k)),
    koOnly:    [...koKeys].filter(k => !campaKeys.has(k)),
    campaOnly: [...campaKeys].filter(k => !koKeys.has(k)),
    brands:    [...new Set((ko?.rows || []).map(r => r.brand).filter(Boolean))],
    packs:     [...new Set((ko?.rows || []).map(r => r.pack).filter(Boolean))],
    koMapping: ko?.mapping,
    koHeaders: ko?.headers,
    fuzzyJoins: [...new Set((ko?.rows || []).filter(r => r.matchedVia).map(r => r.market))],
    // Plants that fell through the mapping table — fix by adding a line to
    // KO_PLANT_TO_BU. Surfaced in the dashboard's diagnostics strip.
    unmappedPlants: [...new Set((ko?.rows || []).filter(r => !r.mapped).map(r => r.plant))],
    plantsByBU: (ko?.rows || []).reduce((acc, r) => {
      (acc[r.market] = acc[r.market] || new Set()).add(r.plant);
      return acc;
    }, {}),
  };
}

/* ══════════════════════════════════════════════════════════════════════
   Metric roll-up
   ══════════════════════════════════════════════════════════════════════ */
export function buildMarketMetrics(data, month, prevMonth) {
  const acc = {};
  const touch = (k) => (acc[k] = acc[k] || {
    key: k, market: data.label[k] || k,
    koTotal: 0, koAff: 0, ko020: 0, campa: 0,
    koTotalPrev: 0, koAffPrev: 0, ko020Prev: 0, campaPrev: 0,
  });

  data.koRows.forEach(r => {
    if (r.key === "allindia") return;
    const a = touch(r.key);
    if (r.month === month)     { a.koTotal += r.vol; if (r.affordable) a.koAff += r.vol; if (r.is020) a.ko020 += r.vol; }
    if (r.month === prevMonth) { a.koTotalPrev += r.vol; if (r.affordable) a.koAffPrev += r.vol; if (r.is020) a.ko020Prev += r.vol; }
  });
  data.campaRows.forEach(r => {
    if (r.key === "allindia") return;
    const a = touch(r.key);
    if (r.month === month)     a.campa += r.vol;
    if (r.month === prevMonth) a.campaPrev += r.vol;
  });

  return Object.values(acc).map(a => ({
    ...a,
    affMix:   a.koTotal ? a.koAff / a.koTotal : 0,
    response: a.campa ? a.koAff / a.campa : null,
    growth:   a.ko020Prev ? a.ko020 / a.ko020Prev - 1
              : (a.koAffPrev ? a.koAff / a.koAffPrev - 1 : null),
    campaGrowth: a.campaPrev ? a.campa / a.campaPrev - 1 : null,
  })).filter(a => a.koTotal > 0 || a.campa > 0);
}